import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FillColorPicker, DEFAULT_FILL } from "@/components/FillColorPicker";
import {
  symbolToPng,
  symbolToSvg,
  compositionToPng,
  compositionToSvg,
  renderCompositionCanvas,
  patternToPng,
  patternToSvg,
  patternText,
  renderPatternCanvas,
  type CompositionLayout,
  type BgShape,
  type Background,
  type PatternType,
} from "@/lib/symbol-asset";
import { FANCY_STYLES, applyStyle } from "@/lib/text-generators";
import { FontStylePicker } from "@/components/FontStylePicker";
import { copyText } from "@/lib/copy";

const PATTERN_TYPES: { id: PatternType; label: string }[] = [
  { id: "row", label: "Row" },
  { id: "grid", label: "Grid" },
  { id: "border", label: "Border" },
];

const LAYOUTS: { id: CompositionLayout; label: string }[] = [
  { id: "stack", label: "Below" },
  { id: "before", label: "Left" },
  { id: "after", label: "Right" },
  { id: "around", label: "Sides" },
];

const BG_SHAPES: { id: BgShape; label: string }[] = [
  { id: "none", label: "None" },
  { id: "circle", label: "Circle" },
  { id: "rounded", label: "Rounded" },
  { id: "square", label: "Square" },
];
const DEFAULT_BG_COLOR = "#7c3aed";

// Caption font styles = the /font-generator fonts (every category except the
// "Symbols & Frames" decorations), grouped for the picker.
// Default export ink follows the theme so the preview stays visible against the
// page: black in light mode, white in dark mode. (Both are real presets, so the
// matching swatch lights up.) Once the user picks any color, we stop overriding.
const DARK_FILL = "#ffffff";

/**
 * True when the dark theme is actually applied — read straight from the `.dark`
 * class on <html> (set before paint by the no-flash script and toggled by every
 * theme change), so it's immune to preference-store hydration timing. SSR-safe:
 * starts false (light) to match server markup, then syncs after mount.
 */
function useIsDarkTheme(): boolean {
  const [dark, setDark] = useState(false);
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setDark(el.classList.contains("dark"));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

interface SymbolExportProps {
  symbol: string;
  /** Used to name the downloaded file, e.g. "black-star". */
  slug: string;
  /** Human-readable name for screen readers. */
  name: string;
  /**
   * Whether the glyph honours a fill color (monochrome). False for multicolor
   * emoji that render in their own colors regardless of fill — in that case the
   * color picker and live preview are hidden as they'd do nothing.
   */
  colorable?: boolean;
}

const SVG_SIZE = 512;
const PNG_SIZES = [128, 512, 1024] as const;

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function SymbolExport({ symbol, slug, name, colorable = true }: SymbolExportProps) {
  const [pngSize, setPngSize] = useState<(typeof PNG_SIZES)[number]>(512);
  // Start at the SSR-safe black; an effect swaps to white in dark mode after
  // hydration so we never produce a server/client markup mismatch.
  const [color, setColor] = useState<string>(DEFAULT_FILL);
  const [busy, setBusy] = useState<null | "svg" | "png" | "copy" | "link">(null);
  const [status, setStatus] = useState("");
  // Optional caption: turns the export into a symbol + text graphic (badge / icon
  // with label / inline lockup).
  const [text, setText] = useState("");
  const [layout, setLayout] = useState<CompositionLayout>("stack");
  const [styleId, setStyleId] = useState("");
  // Apply the chosen /font-generator style to the caption (Unicode font), so the
  // label can be bold, script, fraktur, etc. — empty styleId = plain text.
  const captionText = useMemo(() => {
    if (!styleId) return text;
    const st = FANCY_STYLES.find((s) => s.id === styleId);
    return st ? applyStyle(st, text) : text;
  }, [styleId, text]);
  const caption = captionText.trim();
  const hasText = text.trim().length > 0;
  // Background shape (turns a glyph into an app icon / avatar).
  const [bgShape, setBgShape] = useState<BgShape>("none");
  const [bgColor, setBgColor] = useState<string>(DEFAULT_BG_COLOR);
  const hasBg = bgShape !== "none";
  const bg: Background = { shape: bgShape, color: bgColor };
  // Mode: a single icon (glyph + optional caption) or a repeated pattern.
  const [mode, setMode] = useState<"icon" | "pattern">("icon");
  const [patternType, setPatternType] = useState<PatternType>("row");
  const [pCount, setPCount] = useState(7);
  const [pRows, setPRows] = useState(3);
  const isPattern = mode === "pattern";
  // Fill colour applies to a monochrome symbol, any added text, or a pattern.
  const showFill = colorable || hasText || isPattern;
  // Show the preview whenever there's anything to show.
  const showColor = showFill || hasBg;

  const isDark = useIsDarkTheme();
  const themeDefault = isDark ? DARK_FILL : DEFAULT_FILL;
  const userPicked = useRef(false);

  // Track the theme default until the user explicitly chooses a fill color.
  useEffect(() => {
    if (!userPicked.current) setColor(themeDefault);
  }, [themeDefault]);

  const handleColorChange = useCallback((c: string) => {
    userPicked.current = true;
    setColor(c);
  }, []);

  // Live preview = the actual export, shown as an <img>. With a caption it's the
  // composition (canvas → PNG data URL); plain it's the self-contained SVG.
  const [previewSrc, setPreviewSrc] = useState("");
  useEffect(() => {
    if (!showColor) return;
    let cancelled = false;
    (async () => {
      try {
        let src: string;
        if (isPattern) {
          src = (await renderPatternCanvas({ symbol, type: patternType, count: pCount, rows: pRows, color, bg })).toDataURL("image/png");
        } else if (caption) {
          src = (await renderCompositionCanvas({ symbol, text: caption, color, layout, size: SVG_SIZE, bg })).toDataURL("image/png");
        } else {
          src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(await symbolToSvg(symbol, SVG_SIZE, color, bg));
        }
        if (!cancelled) setPreviewSrc(src);
      } catch {
        /* ignore preview failures */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol, color, showColor, caption, layout, bgShape, bgColor, isPattern, patternType, pCount, pRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadSvg = useCallback(async () => {
    setBusy("svg");
    try {
      const svg = isPattern
        ? await patternToSvg({ symbol, type: patternType, count: pCount, rows: pRows, color, bg })
        : caption
          ? await compositionToSvg({ symbol, text: caption, color, layout, size: SVG_SIZE, bg })
          : await symbolToSvg(symbol, SVG_SIZE, color, bg);
      const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      triggerDownload(blob, `${slug}${isPattern ? `-${patternType}` : ""}.svg`);
      setStatus(`${name} downloaded as SVG.`);
    } catch {
      setStatus("Sorry, the SVG export failed. Please try again.");
    } finally {
      setBusy(null);
    }
  }, [symbol, slug, name, color, caption, layout, bgShape, bgColor, isPattern, patternType, pCount, pRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build the current export as a PNG blob (shared by download + copy-to-clipboard).
  const buildPngBlob = useCallback((): Promise<Blob> => {
    if (isPattern) return patternToPng({ symbol, type: patternType, count: pCount, rows: pRows, color, bg });
    if (caption) return compositionToPng({ symbol, text: caption, color, layout, size: pngSize, bg });
    return symbolToPng(symbol, pngSize, color, bg);
  }, [symbol, pngSize, color, caption, layout, bgShape, bgColor, isPattern, patternType, pCount, pRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPng = useCallback(async () => {
    setBusy("png");
    try {
      const blob = await buildPngBlob();
      triggerDownload(blob, isPattern ? `${slug}-${patternType}.png` : `${slug}-${pngSize}.png`);
      setStatus(`${name} downloaded as PNG.`);
    } catch {
      setStatus("Sorry, the export failed. Please try again.");
    } finally {
      setBusy(null);
    }
  }, [buildPngBlob, slug, name, pngSize, isPattern, patternType]);

  // Copy the rendered image straight to the clipboard (paste into chat/docs/design
  // apps). Pass the blob as a promise so Safari keeps the user-gesture context.
  const canCopyImage = typeof window !== "undefined" && !!navigator.clipboard && typeof ClipboardItem !== "undefined";
  const copyImage = useCallback(async () => {
    if (!canCopyImage) {
      setStatus("This browser can't copy images — use Download instead.");
      return;
    }
    setBusy("copy");
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": buildPngBlob() })]);
      setStatus(`${name} image copied to clipboard.`);
    } catch {
      setStatus("Couldn't copy the image — use Download instead.");
    } finally {
      setBusy(null);
    }
  }, [buildPngBlob, name, canCopyImage]);

  // Shareable link that reproduces the current export settings.
  const copyLink = useCallback(async () => {
    const p = new URLSearchParams();
    if (isPattern) {
      p.set("m", "pattern");
      p.set("pt", patternType);
      p.set("pc", String(pCount));
      if (patternType !== "row") p.set("pr", String(pRows));
    } else if (text.trim()) {
      p.set("t", text);
      if (styleId) p.set("f", styleId);
      p.set("l", layout);
    }
    if (userPicked.current) p.set("c", color);
    if (hasBg) {
      p.set("bs", bgShape);
      p.set("bc", bgColor);
    }
    const qs = p.toString();
    const url = `${window.location.origin}${window.location.pathname}${qs ? `?${qs}` : ""}`;
    setBusy("link");
    try {
      await navigator.clipboard.writeText(url);
      setStatus("Shareable link copied to clipboard.");
    } catch {
      setStatus("Couldn't copy the link.");
    } finally {
      setBusy(null);
    }
  }, [isPattern, patternType, pCount, pRows, text, styleId, layout, color, hasBg, bgShape, bgColor]);

  // On load, restore export settings from a shared link's query params.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if ([...p.keys()].length === 0) return;
    if (p.get("m") === "pattern") setMode("pattern");
    const pt = p.get("pt");
    if (pt && ["row", "grid", "border"].includes(pt)) setPatternType(pt as PatternType);
    const pc = p.get("pc");
    if (pc) setPCount(Math.max(1, Math.min(40, Number(pc) || 1)));
    const pr = p.get("pr");
    if (pr) setPRows(Math.max(1, Math.min(20, Number(pr) || 1)));
    const t = p.get("t");
    if (t != null) setText(t);
    const f = p.get("f");
    if (f && FANCY_STYLES.some((s) => s.id === f)) setStyleId(f);
    const l = p.get("l");
    if (l && ["stack", "before", "after", "around"].includes(l)) setLayout(l as CompositionLayout);
    const c = p.get("c");
    if (c) {
      userPicked.current = true;
      setColor(c);
    }
    const bs = p.get("bs");
    if (bs && ["circle", "rounded", "square"].includes(bs)) setBgShape(bs as BgShape);
    const bc = p.get("bc");
    if (bc) setBgColor(bc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyPatternText = useCallback(async () => {
    const ok = await copyText(patternText({ symbol, type: patternType, count: pCount, rows: pRows }), {
      source: "export-pattern",
      symbol,
    });
    setStatus(ok ? "Pattern copied to clipboard." : "Copy failed — try again.");
  }, [symbol, patternType, pCount, pRows]);

  return (
    <div className="mt-6 rounded-xl border border-border bg-surface/40 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Export
          </h2>
          <p className="mt-1 text-sm">
            Download <span className="font-medium">{name}</span> as SVG or PNG — or add a caption to
            make a labelled icon, badge or sticker.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          {/* Make: a single icon, or a repeated pattern (divider / grid / border). */}
          <div className="mb-3 inline-flex rounded-lg border border-border bg-surface/60 p-0.5" role="group" aria-label="Export mode">
            {(["icon", "pattern"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                aria-pressed={mode === m}
                className={
                  "rounded-md px-3 py-1 text-xs font-semibold capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                  (mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")
                }
              >
                {m === "icon" ? "Icon" : "Pattern"}
              </button>
            ))}
          </div>

          {isPattern && (
            <div className="space-y-3">
              <div>
                <span className="block text-xs font-medium text-muted-foreground">Pattern</span>
                <div className="mt-1.5 inline-flex flex-wrap gap-1.5" role="group" aria-label="Pattern type">
                  {PATTERN_TYPES.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPatternType(p.id)}
                      aria-pressed={patternType === p.id}
                      className={
                        "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                        (patternType === p.id
                          ? "border-primary/50 bg-primary/10 text-foreground"
                          : "border-border bg-surface/60 text-muted-foreground hover:text-foreground")
                      }
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-muted-foreground">
                  {patternType === "row" ? "Count" : "Columns"}
                  <input
                    type="number"
                    min={1}
                    max={40}
                    value={pCount}
                    onChange={(e) => setPCount(Math.max(1, Math.min(40, Number(e.target.value) || 1)))}
                    className="mt-1 block w-20 rounded-md border border-border bg-surface/60 px-2 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </label>
                {patternType !== "row" && (
                  <label className="text-xs text-muted-foreground">
                    Rows
                    <input
                      type="number"
                      min={patternType === "border" ? 2 : 1}
                      max={20}
                      value={pRows}
                      onChange={(e) => setPRows(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                      className="mt-1 block w-20 rounded-md border border-border bg-surface/60 px-2 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                )}
                <button
                  type="button"
                  onClick={copyPatternText}
                  className="rounded-md border border-border bg-surface/60 px-3 py-1.5 text-xs font-semibold transition hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Copy as text
                </button>
              </div>
            </div>
          )}

          {!isPattern && (
            <>
          {/* Add a caption to turn the symbol into a labelled icon / badge. */}
          <label htmlFor={`export-text-${slug}`} className="block text-sm font-medium text-foreground">
            Add text <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <input
            id={`export-text-${slug}`}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="A name, label or caption…"
            autoComplete="off"
            maxLength={48}
            className="mt-1.5 w-full rounded-lg border border-border bg-surface/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />

          {hasText && (
            <div className="mt-3">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Text font</span>
              <FontStylePicker value={styleId} onChange={setStyleId} sample={text} />
            </div>
          )}

          {hasText && (
            <div className="mt-3">
              <span className="block text-xs font-medium text-muted-foreground">Layout</span>
              <div className="mt-1.5 inline-flex flex-wrap gap-1.5" role="group" aria-label="Text layout">
                {LAYOUTS.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setLayout(l.id)}
                    aria-pressed={layout === l.id}
                    className={
                      "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                      (layout === l.id
                        ? "border-primary/50 bg-primary/10 text-foreground"
                        : "border-border bg-surface/60 text-muted-foreground hover:text-foreground")
                    }
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}
            </>
          )}

          {showFill && (
            <div className="mt-4">
              <FillColorPicker color={color} onChange={handleColorChange} />
            </div>
          )}

          <div className="mt-4">
            <span className="block text-xs font-medium text-muted-foreground">Background</span>
            <div className="mt-1.5 inline-flex flex-wrap gap-1.5" role="group" aria-label="Background shape">
              {BG_SHAPES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setBgShape(s.id)}
                  aria-pressed={bgShape === s.id}
                  className={
                    "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                    (bgShape === s.id
                      ? "border-primary/50 bg-primary/10 text-foreground"
                      : "border-border bg-surface/60 text-muted-foreground hover:text-foreground")
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {hasBg && (
            <div className="mt-3">
              <FillColorPicker color={bgColor} onChange={setBgColor} label="Background color" />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={downloadSvg}
              disabled={busy !== null}
              aria-label={`Download ${name} as SVG`}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface/60 px-4 py-2 text-sm font-semibold transition hover:bg-secondary disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {busy === "svg" ? "Preparing…" : "Download SVG"}
            </button>

            <div className="inline-flex items-center gap-2 rounded-md border border-border bg-surface/60 px-2 py-1.5">
              {!isPattern && (
                <>
                  <label htmlFor={`png-size-${slug}`} className="text-xs text-muted-foreground">
                    PNG size
                  </label>
                  <select
                    id={`png-size-${slug}`}
                    value={pngSize}
                    onChange={(e) => setPngSize(Number(e.target.value) as (typeof PNG_SIZES)[number])}
                    className="bg-surface text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring [color-scheme:dark]"
                  >
                    {PNG_SIZES.map((s) => (
                      <option key={s} value={s} className="bg-background text-foreground">
                        {s}px
                      </option>
                    ))}
                  </select>
                </>
              )}
              <button
                type="button"
                onClick={downloadPng}
                disabled={busy !== null}
                aria-label={`Download ${name} as ${pngSize} by ${pngSize} pixel PNG`}
                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {busy === "png" ? "Preparing…" : "Download PNG"}
              </button>
            </div>

            {canCopyImage && (
              <button
                type="button"
                onClick={copyImage}
                disabled={busy !== null}
                aria-label={`Copy ${name} image to clipboard`}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface/60 px-4 py-2 text-sm font-semibold transition hover:bg-secondary disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {busy === "copy" ? "Copying…" : "Copy image"}
              </button>
            )}

            <button
              type="button"
              onClick={copyLink}
              disabled={busy !== null}
              aria-label="Copy a shareable link to this export"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface/60 px-4 py-2 text-sm font-semibold transition hover:bg-secondary disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {busy === "link" ? "Copying…" : "Copy link"}
            </button>
          </div>
        </div>

        {showColor && (
          <div className="flex flex-none flex-col items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Preview</span>
            <div className="export-preview-bg flex h-36 w-36 items-center justify-center overflow-hidden rounded-lg border border-border p-2">
              {/* The preview matches the export exactly (page fonts), so a long
                  caption or inline lockup is letterboxed via object-contain. */}
              {previewSrc && (
                <img
                  src={previewSrc}
                  alt={`Preview of ${name}${hasText ? ` with “${text.trim()}”` : ""} in the selected color`}
                  className="max-h-full max-w-full object-contain"
                  draggable={false}
                />
              )}
            </div>
            <span className="text-[10px] text-muted-foreground">
              {hasBg ? `${bgShape} background` : "transparent background"}
            </span>
          </div>
        )}
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground">
        {hasText
          ? "Fill color applies to the symbol (monochrome) and the text; multicolor emoji keep their own colors. Add a background to make an icon, badge or avatar."
          : colorable
            ? "Fill color applies to monochrome symbols; multicolor emoji keep their own colors. Add a circle/rounded background to make an app icon or avatar. Exports are self-contained; the SVG embeds the glyph's font."
            : "This emoji keeps its own colors. Add a background to turn it into an app icon or avatar. Exports are self-contained and render the same anywhere."}
        {hasBg ? "" : " Without a background, exports are transparent."}
      </p>

      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {status}
      </div>
    </div>
  );
}
